import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { BlobReplicaDebug, PersonAttachment } from "../../api/person/person-attachment";

interface AttachmentBlobDebugProps {
	attachment: PersonAttachment;
}

const replicaColor = (state: string): "success" | "warning" | "error" | "default" => {
	if (state === "verified") return "success";
	if (state === "pending") return "warning";
	if (state === "failed") return "error";
	return "default";
};

const formatBytes = (size: number | null): string => {
	if (size === null) return "?";
	if (size < 1024) return `${size} B`;
	return `${(size / 1024).toFixed(1)} KB`;
};

const ReplicaChip = ({ replica }: { replica: BlobReplicaDebug }) => (
	<Tooltip title={`key: ${replica.key}`}>
		<Chip
			size="small"
			color={replicaColor(replica.state)}
			label={`${replica.location} (${replica.provider}) · ${replica.state}`}
			data-component="blob-replica-chip"
		/>
	</Tooltip>
);

/**
 * Admin-only storage diagnostics for a single CV attachment, shown inline under
 * the "file uploaded" entry in the activity timeline. Renders nothing unless the
 * API populated `blob_debug` (superusers only), so it self-gates — no caller
 * permission check needed. Used to watch the GCS -> Hetzner migration per file.
 */
export const AttachmentBlobDebug = ({ attachment }: AttachmentBlobDebugProps) => {
	const debug = attachment.blob_debug;
	if (!debug) return null;

	return (
		<Box
			sx={{
				mt: 0.5,
				ml: 0.5,
				p: 0.75,
				borderRadius: 1,
				backgroundColor: "action.hover",
				display: "flex",
				flexDirection: "column",
				gap: 0.25,
			}}
			data-component="attachment-blob-debug"
		>
			{debug.blob_id === null ? (
				<Chip
					size="small"
					color="warning"
					variant="outlined"
					label="No blob — legacy, not onboarded"
					data-component="blob-missing-chip"
					sx={{ alignSelf: "flex-start" }}
				/>
			) : (
				<>
					<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
						{debug.replicas.length === 0 ? (
							<Chip
								size="small"
								color="error"
								variant="outlined"
								label="blob has no replicas"
								data-component="blob-no-replicas-chip"
							/>
						) : (
							debug.replicas.map((replica) => (
								<ReplicaChip key={replica.key} replica={replica} />
							))
						)}
					</Stack>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ fontFamily: "monospace" }}
						data-component="blob-meta"
					>
						blob #{debug.blob_id} · {formatBytes(debug.size)} ·{" "}
						{debug.content_type || "?"} · sha256:{" "}
						{debug.sha256 ? debug.sha256.slice(0, 16) : "?"}…
					</Typography>
				</>
			)}

			{debug.attachment_url ? (
				<Tooltip title={debug.attachment_url}>
					<Typography
						variant="caption"
						color="text.secondary"
						noWrap
						sx={{ fontFamily: "monospace", maxWidth: "100%" }}
						data-component="legacy-attachment-url"
					>
						legacy url: {debug.attachment_url}
					</Typography>
				</Tooltip>
			) : null}
		</Box>
	);
};
